import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import UserProject from '~/client/src/shared/models/UserProject'
import { canEditResource } from '~/client/src/shared/resources/canEditResource'
import { IResourceValueRecord } from '~/client/src/shared/resources/definitions'

export type CanEditResource = (
  record: IResourceValueRecord | null | undefined,
) => boolean

/**
 * The current user's `canEditResource` for one resource type, read off the provider like
 * the other shared resource hooks. Where no user or project membership is mounted it lets
 * everything through, leaving the decision to the backend.
 */
export function useCanEditResource(
  resourceTypeId: string | undefined,
): CanEditResource {
  const stores = React.useContext(MobXProviderContext) as any
  const user = stores?.state?.user
  const userProjectsStore = stores?.userProjectsStore
  const companiesStore = stores?.companiesStore

  // Read during render so an observer re-renders once memberships or companies load.
  const isAdmin =
    !!user &&
    !!userProjectsStore &&
    !!UserProject.isAdmin(user, userProjectsStore)
  const companyId =
    user && userProjectsStore
      ? UserProject.getCompanyId(user, userProjectsStore)
      : null
  const userId: string | null = user?.id ?? null
  const isResolvable = !!user && !!userProjectsStore

  return React.useCallback(
    record =>
      !isResolvable ||
      canEditResource({
        editor: { userId, companyId, isAdmin },
        record,
        resourceTypeId,
        getCompanyContactIds: id =>
          companiesStore?.getCompanyById(id)?.responsibleContactIds ?? [],
      }),
    [isResolvable, userId, companyId, isAdmin, resourceTypeId, companiesStore],
  )
}
